import { useRef, useEffect, useState } from 'react'
import { useAdminFetch } from '../hooks/useAdminAPI'
import { PageHeader, AdminButton, ViewState } from '../components'
import styles from './ClusterGraph.module.css'

const CATEGORY_COLORS = {
  groente: { fill: '#4ade80', stroke: '#22c55e', label: 'Groente' },
  fruit:   { fill: '#fb923c', stroke: '#f97316', label: 'Fruit' },
}
const FALLBACK_COLOR = { fill: '#94a3b8', stroke: '#64748b', label: 'Other' }


const LABEL_COLOR = 'rgba(255, 255, 255, 0.8)'

export function ClusterGraph() {
  const { data, loading, error, refresh } = useAdminFetch('/admin/graph')
  const canvasRef = useRef(null)
  const simRef = useRef(null)
  const [hoveredNode, setHoveredNode] = useState(null)
  const [tooltip, setTooltip] = useState(null)

  useEffect(() => {
    if (!data?.nodes?.length || !canvasRef.current) return

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const dpr = window.devicePixelRatio || 1

    function resize() {
      const rect = canvas.parentElement.getBoundingClientRect()
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      canvas.style.width = `${rect.width}px`
      canvas.style.height = `${rect.height}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      return { width: rect.width, height: rect.height }
    }

    let { width, height } = resize()

    // Initialize nodes in a radial layout grouped by category
    const categoryGroups = {}
    data.nodes.forEach(n => {
      if (!categoryGroups[n.category]) categoryGroups[n.category] = []
      categoryGroups[n.category].push(n)
    })

    const categories = Object.keys(categoryGroups)
    const nodes = []

    categories.forEach((cat, ci) => {
      const group = categoryGroups[cat]
      const cx = width * (0.35 + ci * 0.3)
      const cy = height * 0.5

      group.forEach((n, i) => {
        const angle = (i / group.length) * Math.PI * 2
        const r = Math.min(width, height) * 0.25
        nodes.push({
          ...n,
          x: cx + Math.cos(angle) * r * (0.5 + Math.random() * 0.5),
          y: cy + Math.sin(angle) * r * (0.5 + Math.random() * 0.5),
          vx: 0,
          vy: 0,
          radius: Math.max(5, Math.min(22, Math.sqrt(n.productCount) * 3.5)),
        })
      })
    })

    // Build edge lookup with resolved node references
    const edges = data.edges.map(e => ({
      ...e,
      sourceNode: nodes.find(n => n.id === e.source),
      targetNode: nodes.find(n => n.id === e.target),
    })).filter(e => e.sourceNode && e.targetNode)

    // Adjacency map for hover highlighting
    const adjacency = new Map()
    const edgeMap = new Map() // "id1-id2" → edge
    for (const e of edges) {
      if (!adjacency.has(e.source)) adjacency.set(e.source, new Set())
      if (!adjacency.has(e.target)) adjacency.set(e.target, new Set())
      adjacency.get(e.source).add(e.target)
      adjacency.get(e.target).add(e.source)
      const key = e.source < e.target ? `${e.source}-${e.target}` : `${e.target}-${e.source}`
      edgeMap.set(key, e)
    }

    // Category clustering force targets
    const categoryCenters = {}
    categories.forEach((cat, ci) => {
      categoryCenters[cat] = {
        x: width * (0.35 + ci * 0.3),
        y: height * 0.5,
      }
    })

    let animId
    let hovered = null
    let dragging = null
    let dragOffset = { x: 0, y: 0 }
    let alpha = 1

    function simulate() {
      const dt = 0.3
      alpha *= 0.997
      if (alpha < 0.002) alpha = 0.002

      for (const node of nodes) {
        if (node === dragging) continue

        let fx = 0, fy = 0

        // Centering
        fx += (width / 2 - node.x) * 0.0003
        fy += (height / 2 - node.y) * 0.0003

        // Category clustering
        const center = categoryCenters[node.category]
        if (center) {
          fx += (center.x - node.x) * 0.002
          fy += (center.y - node.y) * 0.002
        }

        // Repulsion
        for (const other of nodes) {
          if (other === node) continue
          const dx = node.x - other.x
          const dy = node.y - other.y
          const dist = Math.sqrt(dx * dx + dy * dy) || 1
          const minDist = node.radius + other.radius + 15
          if (dist < minDist * 3) {
            const force = (minDist * minDist) / (dist * dist) * 0.4
            fx += (dx / dist) * force
            fy += (dy / dist) * force
          }
        }

        // Edge attraction (stronger for higher similarity)
        for (const edge of edges) {
          if (edge.source !== node.id && edge.target !== node.id) continue
          const other = edge.sourceNode === node ? edge.targetNode : edge.sourceNode
          const dx = other.x - node.x
          const dy = other.y - node.y
          const dist = Math.sqrt(dx * dx + dy * dy) || 1
          // Higher similarity → shorter ideal distance
          const idealDist = 40 + (1 - edge.similarity) * 200
          const force = (dist - idealDist) * 0.001 * edge.similarity
          fx += (dx / dist) * force
          fy += (dy / dist) * force
        }

        // Boundary
        const margin = 50
        if (node.x < margin) fx += (margin - node.x) * 0.08
        if (node.x > width - margin) fx += (width - margin - node.x) * 0.08
        if (node.y < margin) fy += (margin - node.y) * 0.08
        if (node.y > height - margin) fy += (height - margin - node.y) * 0.08

        node.vx = (node.vx + fx * alpha) * 0.85
        node.vy = (node.vy + fy * alpha) * 0.85
        node.x += node.vx * dt
        node.y += node.vy * dt
      }
    }

    function draw() {
      ctx.clearRect(0, 0, width, height)

      // Draw edges
      for (const edge of edges) {
        const { sourceNode: s, targetNode: t, similarity } = edge
        const isHighlighted = hovered && (s.id === hovered.id || t.id === hovered.id)
        const dimmed = hovered && !isHighlighted

        if (dimmed) continue // skip dim edges entirely for clarity

        ctx.beginPath()
        ctx.moveTo(s.x, s.y)
        ctx.lineTo(t.x, t.y)

        if (isHighlighted) {
          // Color edge by similarity strength
          const hue = similarity > 0.9 ? 120 : similarity > 0.85 ? 60 : 30
          ctx.strokeStyle = `hsla(${hue}, 70%, 55%, ${0.3 + similarity * 0.4})`
          ctx.lineWidth = 1 + similarity * 2.5
        } else {
          ctx.strokeStyle = `rgba(255, 255, 255, ${0.02 + similarity * 0.05})`
          ctx.lineWidth = 0.5 + similarity * 1
        }

        ctx.stroke()

        // Show similarity label on highlighted edges
        if (isHighlighted && similarity > 0.8) {
          const mx = (s.x + t.x) / 2
          const my = (s.y + t.y) / 2
          ctx.font = '500 9px Inter, system-ui, sans-serif'
          ctx.fillStyle = `hsla(0, 0%, 100%, 0.5)`
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(similarity.toFixed(2), mx, my)
        }
      }

      // Draw nodes
      for (const node of nodes) {
        const colors = CATEGORY_COLORS[node.category] || FALLBACK_COLOR
        const isHovered = hovered?.id === node.id
        const isNeighbor = hovered && adjacency.get(hovered.id)?.has(node.id)
        const dimmed = hovered && !isHovered && !isNeighbor

        // No-embedding indicator
        if (!node.hasEmbedding) {
          ctx.beginPath()
          ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2)
          ctx.fillStyle = 'rgba(255, 255, 255, 0.05)'
          ctx.fill()
          ctx.setLineDash([3, 3])
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)'
          ctx.lineWidth = 1
          ctx.stroke()
          ctx.setLineDash([])
          continue
        }

        ctx.beginPath()
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2)

        if (isHovered) {
          ctx.shadowColor = colors.fill
          ctx.shadowBlur = 20
        }

        const opacity = dimmed ? '22' : isHovered ? 'ff' : 'cc'
        ctx.fillStyle = colors.fill + opacity
        ctx.fill()
        ctx.shadowBlur = 0

        ctx.strokeStyle = dimmed ? colors.stroke + '22' : colors.stroke
        ctx.lineWidth = isHovered ? 2.5 : 1.5
        ctx.stroke()

        // Labels
        if (node.radius > 9 || isHovered || isNeighbor) {
          ctx.fillStyle = dimmed ? 'rgba(255,255,255,0.1)' : LABEL_COLOR
          ctx.font = `${isHovered ? '600 12px' : '400 10px'} Inter, system-ui, sans-serif`
          ctx.textAlign = 'center'
          ctx.textBaseline = 'top'
          ctx.fillText(node.name, node.x, node.y + node.radius + 4)
        }
      }

      drawLegend(ctx)
    }

    function drawLegend(ctx) {
      const x = 16, y = 16
      ctx.font = '600 11px Inter, system-ui, sans-serif'

      for (const [i, [, color]] of Object.entries(CATEGORY_COLORS).entries()) {
        const ly = y + i * 22
        ctx.beginPath()
        ctx.arc(x + 6, ly + 6, 5, 0, Math.PI * 2)
        ctx.fillStyle = color.fill
        ctx.fill()
        ctx.fillStyle = LABEL_COLOR
        ctx.textAlign = 'left'
        ctx.textBaseline = 'top'
        ctx.fillText(color.label, x + 18, ly + 1)
      }

      // Threshold info
      const thresholdY = y + Object.keys(CATEGORY_COLORS).length * 22 + 8
      ctx.font = '400 10px Inter, system-ui, sans-serif'
      ctx.fillStyle = 'rgba(255,255,255,0.3)'
      ctx.fillText(`similarity ≥ ${data.meta?.threshold ?? '?'}`, x, thresholdY)
    }

    function findNodeAt(mx, my) {
      for (let i = nodes.length - 1; i >= 0; i--) {
        const n = nodes[i]
        const dx = mx - n.x, dy = my - n.y
        if (dx * dx + dy * dy <= (n.radius + 4) ** 2) return n
      }
      return null
    }

    // Compute top neighbor edges for tooltip
    function getNeighborEdges(nodeId) {
      return edges
        .filter(e => e.source === nodeId || e.target === nodeId)
        .sort((a, b) => b.similarity - a.similarity)
        .slice(0, 5)
        .map(e => {
          const other = e.source === nodeId ? e.targetNode : e.sourceNode
          return { name: other.name, similarity: e.similarity }
        })
    }

    function onMouseMove(e) {
      const rect = canvas.getBoundingClientRect()
      const mx = e.clientX - rect.left
      const my = e.clientY - rect.top

      if (dragging) {
        dragging.x = mx - dragOffset.x
        dragging.y = my - dragOffset.y
        dragging.vx = 0
        dragging.vy = 0
        return
      }

      const node = findNodeAt(mx, my)
      hovered = node
      canvas.style.cursor = node ? 'grab' : 'default'

      if (node) {
        setHoveredNode({ ...node, neighbors: getNeighborEdges(node.id) })
        setTooltip({ x: e.clientX, y: e.clientY })
      } else {
        setHoveredNode(null)
        setTooltip(null)
      }
    }

    function onMouseDown(e) {
      const rect = canvas.getBoundingClientRect()
      const mx = e.clientX - rect.left
      const my = e.clientY - rect.top
      const node = findNodeAt(mx, my)
      if (node) {
        dragging = node
        dragOffset = { x: mx - node.x, y: my - node.y }
        canvas.style.cursor = 'grabbing'
        alpha = 0.3
      }
    }

    function onMouseUp() {
      dragging = null
      canvas.style.cursor = hovered ? 'grab' : 'default'
    }

    function onResize() {
      const dims = resize()
      width = dims.width
      height = dims.height
      const cats = Object.keys(categoryCenters)
      cats.forEach((cat, ci) => {
        categoryCenters[cat] = { x: width * (0.35 + ci * 0.3), y: height * 0.5 }
      })
    }

    canvas.addEventListener('mousemove', onMouseMove)
    canvas.addEventListener('mousedown', onMouseDown)
    canvas.addEventListener('mouseup', onMouseUp)
    canvas.addEventListener('mouseleave', () => {
      hovered = null
      dragging = null
      setHoveredNode(null)
      setTooltip(null)
    })
    window.addEventListener('resize', onResize)

    function tick() {
      simulate()
      draw()
      animId = requestAnimationFrame(tick)
    }
    tick()

    simRef.current = { destroy: () => {
      cancelAnimationFrame(animId)
      canvas.removeEventListener('mousemove', onMouseMove)
      canvas.removeEventListener('mousedown', onMouseDown)
      canvas.removeEventListener('mouseup', onMouseUp)
      window.removeEventListener('resize', onResize)
    }}

    return () => simRef.current?.destroy()
  }, [data])

  if (loading) return <ViewState variant="loading" message="Loading embeddings…" />
  if (error) return <ViewState variant="error" message={error} onRetry={refresh} />

  return (
    <div className={styles.container}>
      <PageHeader
        title="Cluster Proximity"
        subtitle="Semantic similarity via Vectorize embeddings — drag nodes to explore"
        actions={
          <AdminButton
            variant="icon-only"
            icon="refresh"
            label="Refresh graph"
            onClick={refresh}
          />
        }
      />
      <div className={styles.canvasWrap}>
        <canvas ref={canvasRef} className={styles.canvas} />
        {hoveredNode && tooltip && (
          <Tooltip node={hoveredNode} x={tooltip.x} y={tooltip.y} />
        )}
      </div>
      {data?.meta && (
        <div className={styles.stats}>
          <span>{data.meta.withEmbeddings}/{data.meta.totalClusters} clusters with embeddings</span>
          <span className={styles.statDivider}>·</span>
          <span>{data.meta.totalEdges} connections (≥ {data.meta.threshold} similarity)</span>
        </div>
      )}
    </div>
  )
}

function Tooltip({ node, x, y }) {
  return (
    <div className={styles.tooltip} style={{ left: x + 14, top: y - 10 }}>
      <strong>{node.name}</strong>
      <span className={styles.tooltipCategory} data-category={node.category}>
        {node.category}
      </span>
      <div className={styles.tooltipMeta}>
        <span>{node.productCount} products · {node.supermarkets?.join(', ')}</span>
      </div>
      {node.neighbors?.length > 0 && (
        <div className={styles.tooltipNeighbors}>
          <span className={styles.tooltipNeighborLabel}>Most similar:</span>
          {node.neighbors.map((n, i) => (
            <div key={i} className={styles.tooltipNeighborRow}>
              <span>{n.name}</span>
              <span className={styles.tooltipSim}>{(n.similarity * 100).toFixed(0)}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
